import React from 'react';
import { useQuery } from 'react-query';
import LoadingSpinner from '../common/LoadingSpinner';
import ErrorAlert from '../common/ErrorAlert';
import { Message, getMessages } from '../../services/messageService';
import { useAuth } from '../../contexts/AuthContext';

// TODO: Fetch messages using useQuery and messageService.getMessages
// TODO: Implement MessageBubble component
// TODO: Add socket listener for real-time messages
// TODO: Implement auto-scroll to bottom

interface MessageListProps {
    conversationId: number;
}

const MessageList: React.FC<MessageListProps> = ({ conversationId }) => {
    const { user: currentUser } = useAuth();

    // Fetch messages for the selected conversation
    // The query key includes the conversationId so that different conversations
    // have separate cache entries.
    const { 
        data: messages = [], // Default to empty array
        isLoading,
        error,
        // TODO: Add isFetchingPreviousPage, fetchPreviousPage, hasPreviousPage for infinite scroll
    } = useQuery<
        Message[], // Data type
        Error      // Error type
    >(
        ['messages', conversationId], // Query key
        () => getMessages(conversationId), // Query function
        {
            enabled: !!conversationId, // Only run query if conversationId is truthy
            refetchOnWindowFocus: false,
            // TODO: Add getPreviousPageParam and select for infinite scroll later
        }
    );

    if (isLoading) {
        return <LoadingSpinner text="Loading messages..." />;
    }

    if (error) {
        return <ErrorAlert message={error?.message || 'Failed to load messages.'} />;
    }

    return (
        <div className="message-list d-flex flex-column p-2">
            {messages.length > 0 ? (
                messages.map((msg) => {
                    // Determine if the current user sent the message
                    const isSender = msg.senderId === currentUser?.id;

                    return (
                        // Apply alignment and specific class based on sender
                        <div 
                            key={msg.id} 
                            className={`message-bubble-wrapper d-flex ${isSender ? 'justify-content-end' : 'justify-content-start'} mb-2`}
                        >
                            <div 
                                className={`message-bubble p-2 rounded shadow-sm ${isSender ? 'message-sent bg-primary text-white' : 'message-received bg-light'}`}
                                style={{ maxWidth: '75%', wordWrap: 'break-word' }} // Prevent overly wide bubbles
                            >
                                {/* Optionally show sender name for received messages in group chats later */} 
                                {/* {!isSender && <small className="d-block text-muted">{msg.sender?.name || 'User'}</small>} */} 
                                <p className="mb-0 small">{msg.content}</p>
                                <small 
                                    className={`d-block text-end mt-1 ${isSender ? 'text-white-50' : 'text-muted'}`}
                                >
                                    {new Date(msg.createdAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                                </small>
                            </div>
                        </div>
                    );
                })
            ) : (
                <div className="text-center text-muted mt-5">
                    No messages yet. Start the conversation!
                </div>
            )}
        </div>
    );
};

export default MessageList; 